import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';

interface Props {
  projectId: string;
  role: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  // API role values
  PM: 'Product Manager',
  ARCHITECT: 'Architect',
  TECH_LEAD: 'Tech Lead',
  DEVELOPER: 'Developer',
  QA: 'QA Engineer',
  DEVOPS: 'DevOps Engineer',
  STAKEHOLDER: 'Stakeholder',
  // Legacy / alternate keys
  PRODUCT_MANAGER: 'Product Manager',
  LEAD_ENGINEER: 'Lead Engineer',
  BACKEND_ENGINEER: 'Backend Engineer',
  FRONTEND_ENGINEER: 'Frontend Engineer',
  QA_ENGINEER: 'QA Engineer',
  DEVOPS_ENGINEER: 'DevOps Engineer',
  DESIGNER: 'Designer',
};

export default function InviteModal({ projectId, role, onClose, onSuccess }: Props) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      await apiFetch(`/projects/${projectId}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ invitedEmail: trimmed, role }),
      });
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = ROLE_LABELS[role] ?? role;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    background: 'var(--surface-alt)',
    color: 'var(--text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: 480,
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          background: 'var(--surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {success ? (
          <div style={{ padding: 'var(--space-6)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Invite teammate</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 20 }}>
              Inviting for role: <strong style={{ color: 'var(--text)' }}>{roleLabel}</strong>
            </p>
            <div
              style={{
                padding: '12px 16px',
                background: 'rgba(16,185,129,0.08)',
                border: '1px solid rgba(16,185,129,0.3)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                color: 'var(--success)',
              }}
            >
              ✓ Invitation sent!
            </div>
          </div>
        ) : (
          <form onSubmit={e => void handleSubmit(e)} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Scrollable content */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: 'var(--space-6)', paddingBottom: 0 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Invite teammate</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 20 }}>
                Inviting for role: <strong style={{ color: 'var(--text)' }}>{roleLabel}</strong>
              </p>
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: 'block',
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  Email address
                </label>
                <input
                  ref={inputRef}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  required
                  disabled={loading}
                  style={inputStyle}
                  onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                  onBlur={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                />
              </div>

              {error && (
                <div
                  style={{
                    marginBottom: 16, padding: '10px 12px',
                    background: 'rgba(239,68,68,0.06)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-body)', fontSize: 13,
                    color: 'var(--error)',
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            {/* Footer — always visible */}
            <div style={{ flexShrink: 0, display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px var(--space-6) var(--space-6)', borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500,
                  padding: '9px 18px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !email.trim()}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
                  padding: '9px 18px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: 'var(--primary)',
                  color: '#fff',
                  cursor: loading || !email.trim() ? 'not-allowed' : 'pointer',
                  opacity: loading || !email.trim() ? 0.5 : 1,
                }}
              >
                {loading ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
